'use client';

import { useAtomSet, useAtomValue } from '@effect/atom-react';
import { useGridColumnCount } from '@vs-shop/headless/useGridColumnCount';
import { useScrollToItemOnHash } from '@vs-shop/headless/useScrollToItemOnHash';
import type { ProductCardVariant } from '@vs-shop/commerce-ui/productCard';
import {
  ProductsGrid as ProductsGridField,
  ProductsGridItem,
  ProductsGridVariant,
} from '@vs-shop/commerce-ui/productsGrid';
import { EmptyState, EmptyStateVariant } from '@vs-shop/commerce-ui/emptyState';
import type { ListingScopeKey } from '@vs-shop/engine/capability/productListing/listingScope';
import type { GtmListIdTypeT } from '@vs-shop/schemas/gtm/list-types';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useListingCanonical } from '@vs-shop/headless/useListingCanonical';
import { Option } from 'effect';
import type { ListingResult } from '@vs-shop/engine/listing/atom/makeListingAtoms';
import { useIntl } from 'react-intl';

import { rcprofiListingJourney } from '../../../effect/listing/listingJourney';
import { rcprofiListingCanonicalHref } from '../../../effect/listing/listingCanonical';
import type { Banner } from '../../../effect/schema/openapi/backend';
import { toListingCard } from '../../../product/toListingCard';
import { updateUrlOnLinkClick } from '../../../shared/utils/updateUrlOnLinkClick';
import { pageParamName } from '../../../config/urlParams';
import { insertBannersIntoProducts } from '../../../utils/insertBannersIntoProducts';
import { ProductOrBannerType, type ProductOrBanner } from '../../../type/banner';
import { clsxm } from '@vs-shop/utils';
import { cva } from 'class-variance-authority';

import ProductCardSkeleton from '../../server/product/card/ProductCardSkeleton';
import ContactUsProductCard from '../../server/product/card/ContactUsProductCart/ContactUsProductCard';
import ProductListingBannerCarousel from '../../server/banner/ProductListingBannerCarousel';
import { ProductCardTile } from './ProductCardTile';

type Props = {
  readonly scopeKey: ListingScopeKey;
  readonly listType: GtmListIdTypeT;
  readonly productCardVariant: ProductCardVariant;
  readonly banners: ReadonlyArray<Banner>;
};

const bannerSpanVariants = cva('', {
  variants: {
    span: { one: 'col-span-1', two: 'col-span-2' },
  },
});

/** Renders the shared listing result with RCProfi's card and banner choices. */
export const ListingGrid = ({ scopeKey, listType, productCardVariant, banners }: Props) => {
  const intl = useIntl();
  const binding = rcprofiListingJourney.bind(scopeKey);
  useAtomValue(binding.history);
  const current = useAtomValue(binding.current);
  const retry = useAtomSet(binding.retry);
  const listingState = useAtomValue(binding.state);
  const base = useAtomValue(binding.base);
  const vocabulary = useAtomValue(binding.vocabulary);
  useListingCanonical(rcprofiListingCanonicalHref(base, listingState, vocabulary));
  useScrollToItemOnHash();
  const [gridRef, columnCount] = useGridColumnCount<HTMLUListElement>();

  const renderProducts = ({ products, firstPage, state, failedPage }: ListingResult) => {
    if (products.total === 0) {
      return (
        <EmptyState
          id="listing-products-grid"
          variant={EmptyStateVariant.INLINE}
          className="p-4 text-xl"
        >
          {intl.formatMessage({
            id: 'component.client.products.productsGrid.noProducts',
            defaultMessage: 'Nenalezeny žádné produkty',
          })}
        </EmptyState>
      );
    }

    const cards = products.items.map(toListingCard);
    const withBanners =
      firstPage === 1 && banners.length > 0 && columnCount > 0
        ? insertBannersIntoProducts(cards, banners, columnCount)
        : cards.map((product): ProductOrBanner<typeof product> => ({
            type: ProductOrBannerType.Product,
            product,
          }));
    const appended = firstPage < Option.getOrElse(failedPage, () => state.page);
    let productIndex = 0;

    return (
      <ProductsGridField
        id="listing-products-grid"
        ref={gridRef}
        variant={ProductsGridVariant.LISTING}
      >
        {withBanners.map((item, index) => {
          if (item.type === ProductOrBannerType.Banner) {
            return (
              <ProductsGridItem
                key={item.banners[0]?.id ?? `banner-${String(index)}`}
                className={clsxm(bannerSpanVariants({ span: columnCount >= 4 ? 'two' : 'one' }))}
              >
                <ProductListingBannerCarousel banners={item.banners} />
              </ProductsGridItem>
            );
          }

          const productPage = firstPage + Math.floor(productIndex / products.itemsPerPage);
          productIndex += 1;
          return (
            <ProductCardTile
              key={item.product.code}
              product={item.product}
              listType={listType}
              variant={productCardVariant}
              hashIdentifier={item.product.slug}
              onClick={() => {
                if (appended) updateUrlOnLinkClick(productPage, pageParamName, item.product.slug);
              }}
            />
          );
        })}
        {products.lastPage === undefined || state.page >= products.lastPage ? (
          <ProductsGridItem key="contactUs">
            <ContactUsProductCard />
          </ProductsGridItem>
        ) : null}
      </ProductsGridField>
    );
  };
  const renderInitial = () =>
    AsyncResult.builder(current)
      .onInitial(() => (
        <ProductsGridField
          id="listing-products-grid"
          ref={gridRef}
          variant={ProductsGridVariant.LISTING}
        >
          {Array.from({ length: 16 }, (_, index) => (
            <ProductsGridItem key={`skeleton-${String(index)}`}>
              <ProductCardSkeleton variant={productCardVariant} />
            </ProductsGridItem>
          ))}
        </ProductsGridField>
      ))
      .render();
  const error = (
    <p role="alert" data-slot="listing-error">
      {intl.formatMessage({
        id: 'Listing.error',
        defaultMessage: 'Produkty se nepodařilo načíst.',
      })}
      <button
        type="button"
        onClick={() => retry(undefined)}
        className="ml-2 min-h-[44px] min-w-[44px] underline focus-visible:outline-2"
      >
        {intl.formatMessage({
          id: 'component.client.error.globalErrorContent.button.retry',
          defaultMessage: 'Zkusit znovu',
        })}
      </button>
    </p>
  );
  return (
    <div data-slot="listing-grid" aria-busy={current.waiting}>
      {AsyncResult.isFailure(current) ? error : null}
      {Option.match(AsyncResult.value(current), {
        onNone: renderInitial,
        onSome: renderProducts,
      })}
    </div>
  );
};
